import {
  AdviseEngagement,
  AdviseSupplier,
  AdviseLineItem,
  AdviseQuote,
  DEFAULT_PRICE_CATEGORIES
} from '../../../types/advise';

export type Billing = 'monthly' | 'one-time';

export interface CategoryTotals {
  monthly: number;
  oneTime: number;
}

type TelephonyOverride = { quantity: number; rate: number } | undefined;

const TELCO_USAGE = 'Telco / Usage';

export const categoryTotal = (
  lineItems: AdviseLineItem[],
  supplierMapping: Record<string, string>,
  telephonyOverride: TelephonyOverride,
  category: string,
  billing: Billing
): number => {
  let total = lineItems.reduce((sum, item) => {
    const assignedCat = supplierMapping[item.item_name] || '';
    return assignedCat === category && item.billing_cycle === billing ? sum + (item.total_price || 0) : sum;
  }, 0);

  if (category === TELCO_USAGE && billing === 'monthly' && telephonyOverride) {
    total += telephonyOverride.quantity * telephonyOverride.rate;
  }
  return total;
};

export const supplierTotal = (
  lineItems: AdviseLineItem[],
  supplierMapping: Record<string, string>,
  telephonyOverride: TelephonyOverride,
  categories: string[],
  billing: Billing
): number =>
  categories.reduce((sum, cat) => sum + categoryTotal(lineItems, supplierMapping, telephonyOverride, cat, billing), 0);

export const sumCategoryTotals = (byCategory: Record<string, CategoryTotals>): CategoryTotals =>
  Object.values(byCategory).reduce(
    (acc, t) => ({ monthly: acc.monthly + (t?.monthly || 0), oneTime: acc.oneTime + (t?.oneTime || 0) }),
    { monthly: 0, oneTime: 0 }
  );

export const yearOneTotal = (byCategory: Record<string, CategoryTotals>): number => {
  const { monthly, oneTime } = sumCategoryTotals(byCategory);
  return monthly * 12 + oneTime;
};

const emptyCategoryTotals = (): Record<string, CategoryTotals> => {
  const totals: Record<string, CategoryTotals> = {};
  DEFAULT_PRICE_CATEGORIES.forEach((cat) => {
    totals[cat] = { monthly: 0, oneTime: 0 };
  });
  return totals;
};

const matchSupplierQuote = (quotes: AdviseQuote[], supplier: AdviseSupplier): AdviseQuote | undefined =>
  quotes.find((q) => {
    const qName = q.supplier_name?.toLowerCase();
    const vName = supplier.supplier_name?.toLowerCase();
    return qName && vName && (qName.includes(vName) || vName.includes(qName));
  });

const supplierPricingTotals = (
  engagement: AdviseEngagement,
  supplier: AdviseSupplier
): Record<string, CategoryTotals> => {
  const totals = emptyCategoryTotals();
  const supplierMapping = (engagement.line_item_mapping || {})[supplier.id] || {};
  const quote = matchSupplierQuote(engagement.uploaded_quotes || [], supplier);

  quote?.extracted_pricing?.line_items?.forEach((item) => {
    const category = supplierMapping[item.item_name] || 'Other';
    if (!totals[category]) totals[category] = { monthly: 0, oneTime: 0 };
    if (item.billing_cycle === 'monthly') totals[category].monthly += item.total_price || 0;
    else if (item.billing_cycle === 'one-time') totals[category].oneTime += item.total_price || 0;
  });

  const override = (engagement.telephony_usage_overrides || {})[supplier.id];
  if (override?.quantity && override?.rate) {
    if (!totals[TELCO_USAGE]) totals[TELCO_USAGE] = { monthly: 0, oneTime: 0 };
    totals[TELCO_USAGE].monthly += override.quantity * override.rate;
  }
  return totals;
};

export const buildSupplierPricingTotals = (
  engagement: AdviseEngagement,
  suppliers: AdviseSupplier[]
): Record<string, Record<string, CategoryTotals>> => {
  const result: Record<string, Record<string, CategoryTotals>> = {};
  suppliers.forEach((supplier) => {
    result[supplier.id] = supplierPricingTotals(engagement, supplier);
  });
  return result;
};
